/**
 * 홈 페이지 전용 수평 네비게이션 바 (메가 메뉴)
 *
 * 단일 통합 드롭다운 패널 방식:
 * - nav 영역 내 어느 버튼에 hover하더라도 전체 패널이 함께 나타남
 * - nav + 패널 영역 모두 벗어나야 패널이 사라짐
 * - 패널은 4개 컬럼(농약 / 동물용의약품 / 기준규격 / 게시판)으로 구성
 *
 * position: sticky; top: 64px (헤더 아래 고정)
 * 순수 CSS hover 제어 - Ant Design Dropdown 사용 안함
 */
import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import useLanguage from '../../hooks/useLanguage';
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
import { buildNavGroups, filterNavGroups } from '../../constants/navGroups';

// ─── 다국어 텍스트 ─────────────────────────────────────
const TEXTS = {
  ko: {
    home: '홈',
    prd: '농약',
    prdInfo: '농약정보',
    mrl: '잔류허용기준',
    food: '식품',
    adi: 'ADI',
    using: '사용현황',
    analysis: '표준품',
    analysisLegacy: '표준품(구)',
    reports: '보고서',
    progress: '수입식품 중 농약잔류허용기준 설정진행사항',
    prdUsageInfo: '농약 사용정보',
    vdUsageInfo: '동약 사용정보',
    vd: '동물용의약품',
    vdInfo: '동물용의약품정보',
    std: '표준품',
    standards: '표준품 현황',
    applications: '표준품 신청',
    requests: '기준요청',
    companies: '업체관리',
    statuses: '현황',
    units: '단위',
    doc: '게시판',
    articles: '공지/자료',
    relatedSites: '관련기관',
    proposals: '안전성보고서',
    admin: '관리자',
    dashboard: '대시보드',
    users: '사용자 관리',
    authTemplates: '권한 템플릿',
    visitors: '방문자 현황',
    audit: '감사 로그',
    workflow: '워크플로우',
    notifications: '공지사항',
    batch: '배치 작업',
    quality: '품질 관리',
    popups: '팝업 관리',
    prdBoard: '농약 게시판',
    vdBoard: '동물용의약품 게시판',
    // admin accordion 그룹 레이블
    adminAuth: '인증 관리',
    adminBase: '공통 마스터 관리',
    adminPrdFood: '농약 식품 관리',
    adminVdFood: '동약 식품 관리',
    adminSystem: '시스템 관리',
    // 기준 정보 sub-items
    masterCompanies: '제조회사',
    masterUnits: '단위',
    masterStatuses: '상태',
    masterCountries: '국가',
    masterProvinces: '소속',
    // 식품 sub-items
    foodClassLarge: '식품 대분류',
    foodClassMiddle: '식품 중분류',
    foodClassSmall: '식품 소분류',
    foodMaster: '식품 마스터',
    // 시스템 관리 sub-items
    systemMaintenance: '유지보수 이력',
  },
  en: {
    home: 'Home',
    prd: 'Pesticides',
    prdInfo: 'Pesticide Info',
    mrl: 'MRL',
    food: 'Food',
    adi: 'ADI',
    using: 'Usage',
    analysis: 'Analysis',
    analysisLegacy: 'Analysis (Legacy)',
    reports: 'Reports',
    progress: 'MRL Progress for Imported Foods',
    prdUsageInfo: 'Pesticide Usage Info',
    vdUsageInfo: 'Vet Drug Usage Info',
    vd: 'Vet. Drugs',
    vdInfo: 'Vet Drug Info',
    std: 'Standard',
    standards: 'Standard Status',
    applications: 'Standard Request',
    requests: 'Requests',
    companies: 'Companies',
    statuses: 'Status',
    units: 'Units',
    doc: 'Board',
    articles: 'Articles',
    relatedSites: 'Related Sites',
    proposals: 'Safety Reports',
    admin: 'Admin',
    dashboard: 'Dashboard',
    users: 'User Mgmt',
    authTemplates: 'Auth Templates',
    visitors: 'Visitors',
    audit: 'Audit Log',
    workflow: 'Workflow',
    notifications: 'Notifications',
    batch: 'Batch Jobs',
    quality: 'Quality',
    popups: 'Popups',
    prdBoard: 'Pesticide Board',
    vdBoard: 'Vet Drug Board',
    // admin accordion group labels
    adminAuth: 'Authentication',
    adminBase: 'Common Master Data',
    adminPrdFood: 'PRD Food',
    adminVdFood: 'VD Food',
    adminSystem: 'System Mgmt',
    // Base data sub-items
    masterCompanies: 'Companies',
    masterUnits: 'Units',
    masterStatuses: 'Statuses',
    masterCountries: 'Countries',
    masterProvinces: 'Provinces',
    // Food sub-items
    foodClassLarge: 'Food Class (Large)',
    foodClassMiddle: 'Food Class (Middle)',
    foodClassSmall: 'Food Class (Small)',
    foodMaster: 'Food Master',
    // System sub-items
    systemMaintenance: 'Maintenance Log',
  },
};

/** CSS: 메가 메뉴 */
const NAV_STYLES = `
  /* ─ 전체 래퍼 (hover 감지 + 드롭다운 포지셔닝 기준) ─ */
  .hnb-wrapper {
    position: relative;
  }

  /* ─ 네비게이션 바 ─ */
  .hnb-nav {
    display: flex;
    align-items: stretch;
    min-height: 46px; /* 긴 메뉴 라벨 줄바꿈 시 클리핑 방지 (고정 height -> min) */
  }

  /* ─ 공통 버튼 ─ */
  .hnb-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.85);
    padding: 0 16px;
    min-height: 46px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: normal;
    overflow: visible;
    min-width: 0;
    transition: background 0.2s, color 0.2s;
    border-bottom: 2px solid transparent;
  }

  /* 메가 열릴 때 모든 버튼 강조 */
  .hnb-wrapper.is-open .hnb-btn {
    color: #fff;
  }

  /* 개별 버튼 hover */
  .hnb-btn:hover {
    background: rgba(255, 255, 255, 0.14);
    border-bottom-color: rgba(255, 255, 255, 0.5);
  }

  /* 그룹 버튼 - 균등 분배 */
  .hnb-group-btn {
    flex: 1;
    min-width: 0;
    justify-content: center;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  }

  /* 버튼 레이블 - 길어지면 줄바꿈 */
  .hnb-btn-label {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: keep-all;
    line-height: 1.18;
    min-width: 0;
  }
  .hnb-group-btn:last-of-type {
    border-right: 1px solid rgba(255, 255, 255, 0.08);
  }

  /* ─ 메가 드롭다운 패널 ─ */
  .hnb-mega {
    display: none;
    position: absolute;
    top: 46px;
    left: 0;
    right: 0;
    z-index: 300;
    max-height: calc(100vh - 130px); /* 낮은 해상도에서 패널 하단 잘림 방지 */
    overflow-y: auto;
    background: #fff;
    border-top: 3px solid #0A7C66;
    border-bottom: 1px solid #e8e8e8;
    border-left: 1px solid #e8e8e8;
    border-right: 1px solid #e8e8e8;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
    padding: 0;
  }

  /* ─ 컬럼 ─ */
  .hnb-col {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    padding: 0;
    border-right: 1px solid #f0f0f0;
  }
  .hnb-col:last-child {
    border-right: none;
  }

  /* 컬럼 헤더 (그룹명) */
  .hnb-col-title {
    display: block;
    padding: 10px 20px 8px;
    font-size: 13px;
    font-weight: 700;
    color: #0A7C66;
    background: #f8fbfa;
    border-bottom: 1px solid #e8f5f0;
    letter-spacing: 0.2px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: keep-all;
    line-height: 1.25;
    text-align: center;
  }

  /* ─ 드롭다운 아이템 ─ */
  .hnb-item {
    display: block;
    width: 100%;
    padding: 9px 20px;
    min-height: 38px;
    background: none;
    border: none;
    text-align: center;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.72);
    cursor: pointer;
    font-family: inherit;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: keep-all;
    line-height: 1.28;
    transition: background 0.12s, color 0.12s;
  }
  .hnb-item:hover {
    background: #eef8f5;
    color: #0A7C66;
  }

  /* ─ 서브 메뉴 섹션 헤더 (nested 부모 — 클릭으로 펼침/접힘) ─ */
  .hnb-subtitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 9px 20px;
    font-size: 12.5px;
    font-weight: 700;
    color: #065A4A;
    background: #f8fbfa;
    border: none;
    border-top: 1px dashed #d5ebe3;
    border-bottom: 1px dashed #d5ebe3;
    margin-top: 4px;
    text-align: center;
    letter-spacing: 0.3px;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s;
  }
  .hnb-subtitle-label {
    flex: 1;
    text-align: center;
    padding-left: 18px; /* 오른쪽 아이콘 공간 균형 보정 */
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: keep-all;
    line-height: 1.25;
  }
  .hnb-subtitle:hover {
    background: #eef8f5;
    color: #0A7C66;
  }
  .hnb-subtitle-icon {
    font-size: 12px;
    opacity: 0.75;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    transition: color 0.15s, opacity 0.15s;
  }
  .hnb-subtitle:hover .hnb-subtitle-icon {
    opacity: 1;
  }
  .hnb-subtitle.is-expanded {
    background: #eef8f5;
    border-bottom-style: solid;
    border-bottom-color: #b4d6cc;
  }
  .hnb-subtitle.is-expanded .hnb-subtitle-icon {
    color: #0A7C66;
    opacity: 1;
  }

  /* ─ 서브 메뉴 컨테이너 (접힘/펼침 애니메이션) ─ */
  .hnb-subitem-group {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.3s ease;
  }
  .hnb-subitem-group.is-expanded {
    max-height: 600px; /* 충분히 큰 값 */
  }

  /* ─ 서브 메뉴 아이템 (nested 자식) ─ */
  .hnb-sub-item {
    padding-left: 32px !important;
    padding-right: 32px !important;
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.65);
    position: relative;
  }
  .hnb-sub-item::before {
    content: '';
    position: absolute;
    left: 18px;
    top: 50%;
    width: 6px;
    height: 1px;
    background: #b4d6cc;
  }
  .hnb-sub-item:hover {
    background: #e0f2ec;
    color: #0A7C66;
  }
  .hnb-sub-item:hover::before {
    background: #0A7C66;
  }

  /* 다크 모드 - 서브 메뉴 */
  html[data-theme="dark"] .hnb-subtitle {
    color: #3dba9c;
    background: #1a1a1a;
    border-top-color: #2a3a36;
    border-bottom-color: #2a3a36;
  }
  html[data-theme="dark"] .hnb-subtitle:hover {
    background: #1f2d29;
    color: #5ed4b5;
  }
  html[data-theme="dark"] .hnb-subtitle.is-expanded {
    background: #1f2d29;
    border-bottom-color: #2a4a42;
  }
  html[data-theme="dark"] .hnb-subtitle.is-expanded .hnb-subtitle-icon {
    color: #3dba9c;
  }
  html[data-theme="dark"] .hnb-sub-item {
    color: rgba(255, 255, 255, 0.6);
  }
  html[data-theme="dark"] .hnb-sub-item::before {
    background: #3a5a52;
  }
  html[data-theme="dark"] .hnb-sub-item:hover {
    background: #1f2d29;
    color: #3dba9c;
  }
  html[data-theme="dark"] .hnb-sub-item:hover::before {
    background: #3dba9c;
  }

  /* ── 다크 모드 ── */
  html[data-theme="dark"] .hnb-mega {
    background: #1f1f1f;
    border-top-color: #1a8a70;
    border-bottom-color: #333;
    border-left-color: #333;
    border-right-color: #333;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }
  html[data-theme="dark"] .hnb-col {
    border-right-color: #333;
  }
  html[data-theme="dark"] .hnb-col-title {
    color: #3dba9c;
    background: #1a1a1a;
    border-bottom-color: #333;
  }
  html[data-theme="dark"] .hnb-item {
    color: rgba(255, 255, 255, 0.72);
  }
  html[data-theme="dark"] .hnb-item:hover {
    background: #262b2a;
    color: #3dba9c;
  }
`;

/**
 * 홈 전용 메가 메뉴 네비게이션 바
 */
const HomeNavBar = () => {
  const { isMobile } = useResponsive();
  const history = useHistory();
  const { t } = useLanguage(TEXTS);
  const { user, hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // 아코디언 펼침 상태 — 한 번에 하나만 열림 (클래식 아코디언)
  // 기본: 아무것도 안 열림 (null), 다른 항목 클릭 시 이전 항목 자동 닫힘
  // 메가 메뉴 재오픈 시에도 사용자의 펼침 상태 유지
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  const go = (path) => {
    setIsOpen(false);
    history.push(path);
  };

  /**
   * 아코디언 토글 — 한 번에 하나만 열리는 "exclusive accordion".
   * 이미 열려있는 항목을 다시 클릭하면 닫히고, 다른 항목을 클릭하면
   * 기존 열린 항목은 자동으로 닫힌 후 새 항목이 열린다.
   */
  const toggleSubmenu = (key, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setExpandedSubmenu((prev) => (prev === key ? null : key));
  };

  // ─── 드롭다운 그룹 정의 (공통 모듈 사용) ───────────────
  // NAV_GROUPS와 필터링 로직은 frontend/src/constants/navGroups.js로 추출되었다.
  // AppHeader의 모바일 Drawer와 동일한 단일 소스를 공유한다.
  //
  // React Hooks 규칙(조건부 return 전에 모든 hooks 호출)을 지키기 위해
  // useMemo 기반 필터링을 먼저 수행한 뒤, isMobile 조기 반환을 수행한다.
  const filteredNavGroups = useMemo(() => {
    const groups = buildNavGroups(t);
    return filterNavGroups(groups, user, hasPermission);
  }, [t, user, hasPermission]);

  // 모바일: AppHeader의 햄버거 Drawer로 대체 (조기 반환 — 모든 hooks 호출 후)
  if (isMobile) return null;

  // 접근 가능한 그룹이 하나도 없으면 네비게이션 바 자체를 숨긴다.
  if (filteredNavGroups.length === 0) return null;

  return (
    <>
      {/* 메가 메뉴 CSS 주입 */}
      <style>{NAV_STYLES}</style>

      <style>{`@keyframes navbarFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      <div
        style={{
          background: '#00264d',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          position: 'fixed',
          top: 63,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 99,
          animation: 'navbarFadeIn 0.25s ease both',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 8px' }}>

          {/* ─ 래퍼: mouse enter/leave로 메가 패널 제어 ─ */}
          <div
            className={`hnb-wrapper${isOpen ? ' is-open' : ''}`}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >

            {/* ── 상단 네비게이션 바 ── */}
            <nav className="hnb-nav">

                {/* 그룹 버튼 (균등 분배, 클릭 없음 - hover로 패널 제어) */}
              {filteredNavGroups.map((group) => (
                <button key={group.key} className="hnb-btn hnb-group-btn">
                  <span className="hnb-btn-label">{group.label}</span>
                </button>
              ))}

            </nav>

            {/* ── 메가 드롭다운 패널 ── */}
            <div className="hnb-mega" style={{ display: isOpen ? 'flex' : 'none' }}>
              {filteredNavGroups.map((group) => (
                <div key={group.key} className="hnb-col">
                  {/* 컬럼 헤더 */}
                  <span className="hnb-col-title">{group.label}</span>
                  {/* 서브 메뉴 아이템 — children이 있으면 아코디언으로 렌더 */}
                  {group.items.map((item, idx) => {
                    if (item.children) {
                      const parentKey = item.key || `${group.key}-nested-${idx}`;
                      const isExpanded = expandedSubmenu === parentKey;
                      return (
                        <React.Fragment key={parentKey}>
                          {/* 부모 섹션 헤더 — 클릭 시 펼침/접힘 토글 */}
                          <button
                            type="button"
                            className={`hnb-subtitle${isExpanded ? ' is-expanded' : ''}`}
                            onClick={(e) => toggleSubmenu(parentKey, e)}
                            aria-expanded={isExpanded}
                          >
                            <span className="hnb-subtitle-label">{item.label}</span>
                            <span className="hnb-subtitle-icon">
                              {isExpanded ? <MinusOutlined /> : <PlusOutlined />}
                            </span>
                          </button>
                          {/* 자식 아이템 컨테이너 — max-height 애니메이션 */}
                          <div className={`hnb-subitem-group${isExpanded ? ' is-expanded' : ''}`}>
                            {item.children.map((child) => (
                              <button
                                key={child.key}
                                className="hnb-item hnb-sub-item"
                                onClick={() => child.external
                                  ? window.open(child.key, '_blank', 'noopener')
                                  : go(child.key)}
                              >
                                {child.label}
                              </button>
                            ))}
                          </div>
                        </React.Fragment>
                      );
                    }
                    return (
                      <button
                        key={item.key}
                        className="hnb-item"
                        onClick={() => item.external ? window.open(item.key, '_blank', 'noopener') : go(item.key)}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default HomeNavBar;
